import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from './SEOHead';
import LandingFooter from './LandingFooter';
import NotFound from './NotFound';
import RichText from './RichText';
import { POSTS, getPost } from '../content/blogPosts';
import './landing.css';

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';
const BASE_URL = 'https://digihr.in';

export const formatDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const buildSchema = (p) => [
  {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: p.title,
    description: p.description,
    datePublished: p.published,
    dateModified: p.updated,
    url: `${BASE_URL}/blog/${p.slug}`,
    mainEntityOfPage: `${BASE_URL}/blog/${p.slug}`,
    image: `${BASE_URL}/logo512.png`,
    author: { '@type': 'Organization', name: 'DigiHR', url: `${BASE_URL}/` },
    publisher: { '@type': 'Organization', name: 'DigiHR', logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo512.png` } },
    inLanguage: 'en-IN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: p.title, item: `${BASE_URL}/blog/${p.slug}` },
    ],
  },
];

const text = { fontSize: 17, color: '#3a3f4a', lineHeight: 1.8, marginBottom: 20 };

const Block = ({ block: [type, content] }) => {
  switch (type) {
    case 'h2':
      return <h2 style={{ ...serif, fontSize: 'clamp(24px,2.6vw,30px)', lineHeight: 1.2, letterSpacing: '-.02em', color: '#0d1117', margin: '44px 0 16px' }}>{content}</h2>;
    case 'ul':
    case 'ol': {
      const Tag = type;
      return (
        <Tag style={{ ...text, paddingLeft: 24, listStyle: type === 'ul' ? 'disc' : 'decimal' }}>
          {content.map((item, i) => <li key={i} style={{ marginBottom: 10 }}><RichText text={item} /></li>)}
        </Tag>
      );
    }
    case 'note':
      return <div style={{ ...text, fontSize: 15, background: '#f0f4ff', border: '1px solid #d0e0ff', borderRadius: 14, padding: '16px 20px' }}><RichText text={content} /></div>;
    default:
      return <p style={text}><RichText text={content} /></p>;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;

  const related = POSTS.filter(p => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <SEOHead title={post.title} description={post.description} keywords={post.keywords} canonical={`/blog/${post.slug}`} ogType="article" schema={buildSchema(post)} />

      <article>
        <header style={{ padding: '60px 5% 40px', background: 'linear-gradient(160deg,#f0f4ff 0%,#ffffff 100%)', borderBottom: '1px solid #e4e7ed' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: '#717888', marginBottom: 16 }}>
              <Link to="/" style={{ color: '#717888', textDecoration: 'none' }}>Home</Link> / <Link to="/blog" style={{ color: '#717888', textDecoration: 'none' }}>Blog</Link> / <span style={{ color: '#3a3f4a' }}>{post.tag}</span>
            </nav>
            <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: 100, background: '#e8efff', color: ACCENT, marginBottom: 14 }}>{post.tag}</span>
            <h1 style={{ ...serif, fontSize: 'clamp(32px,4vw,48px)', lineHeight: 1.12, letterSpacing: '-.03em', color: '#0d1117', marginBottom: 16 }}>{post.title}</h1>
            <p style={{ fontSize: 18, color: '#3a3f4a', lineHeight: 1.6, marginBottom: 18 }}>{post.description}</p>
            <div style={{ fontSize: 13, color: '#717888', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>By the DigiHR team</span>
              <time dateTime={post.updated}>Updated {formatDate(post.updated)}</time>
              <span>{post.readMins} min read</span>
            </div>
          </div>
        </header>

        <div style={{ padding: '48px 5% 40px' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            {post.body.map((block, i) => <Block key={i} block={block} />)}

            <div style={{ marginTop: 48, padding: 28, borderRadius: 20, background: '#0d1117', color: '#fff' }}>
              <h2 style={{ ...serif, fontSize: 24, color: '#fff', marginBottom: 8 }}>Run HR in one place</h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,.65)', lineHeight: 1.6, marginBottom: 18 }}>GPS attendance, payroll, leave, performance and more — built for Indian businesses. 30-day free trial.</p>
              <Link to="/contact" style={{ display: 'inline-flex', padding: '10px 22px', borderRadius: 12, background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>Book a free Demo →</Link>
            </div>
          </div>
        </div>
      </article>

      <section style={{ padding: '20px 5% 80px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#717888', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 16 }}>Keep reading</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }} className="related-grid-resp">
            {related.map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} style={{ display: 'block', padding: 22, borderRadius: 16, border: '1px solid #e4e7ed', textDecoration: 'none', background: '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: ACCENT, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>{p.tag}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0d1117', lineHeight: 1.4 }}>{p.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />

      <style>{`@media(max-width:900px){ .related-grid-resp{ grid-template-columns:1fr !important; } }`}</style>
    </>
  );
};

export default BlogPost;
